import { useState } from 'react'
import { Alert, Button, Group, Select, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useActOnRequest, useBackstopAssignees, useRequestHandlingGate } from '../../hooks/useRequestBackstop'
import type { BackstopAction, BackstopActionKind } from '../../services/requestBackstopApi'
import type { BackstopRequestDetail } from '../../types/requestBackstop'
import {
  BACKSTOP_NO_APPROVE, dueAtFromDateInput, interventionControls, interventionErrorText, interventionToast, routeLabel,
  showInterventionPanel,
} from './requestBackstopFormat'
import type { InterventionSpec } from './requestBackstopFormat'

/**
 * The interventions a Lighthouse administrator may make on one request (Cartographer doc 15 §7): force close, reassign,
 * reroute, a corrected due date, and platform access's forward, attest and refuse — each only where the server's matrix
 * offers it for this request's state.
 *
 * - The controls render ONLY on the write gate's `admin` state (the catalogue's server-evaluated `canHandleRequests`,
 *   never a token role), and only on a request that has not ended. A `denied` gate says why; `pending` and `unknown`
 *   show nothing. Every act is sent bearer-only.
 * - There is no approve, and no refusal of content access: the server offers neither to this surface.
 * - A refusal stays on screen, worded by the store's code, then the status. A 409 has already re-read the request.
 */
export function InterventionPanel({ detail }: { detail: BackstopRequestDetail }) {
  const gate = useRequestHandlingGate()
  const act = useActOnRequest()
  const [open, setOpen] = useState<BackstopActionKind | null>(null)

  const controls = interventionControls(gate, detail)
  const spec = controls.actions.find((a) => a.action === open) ?? null

  const send = (spec: InterventionSpec, primary: BackstopAction, then: BackstopAction | null) => {
    act.mutate(primary, {
      onSuccess: (result) => {
        if (then) {
          // Reroute, then give them until …: two calls, because rerouting changes no clock (doc 15 §7.1).
          act.mutate(then, {
            onSuccess: (after) => {
              notifications.show({ ...interventionToast(spec, after), color: 'green' })
              setOpen(null)
            },
          })
          return
        }
        notifications.show({ ...interventionToast(spec, result), color: 'green' })
        setOpen(null)
      },
    })
  }

  // A NotOnBackstop refusal (409) has already re-read the request, which now offers nothing — the panel stays, so the
  // refusal's own words ("… Reload to see where it is now.") sit beside where it went.
  if (!showInterventionPanel(controls, act.isError)) {
    return null
  }

  return (
    <Stack gap="sm" data-backstop-interventions="">
      <Stack gap={4}>
        <Text size="sm" fw={600}>Intervene</Text>
        <Text size="xs" c="dimmed">{BACKSTOP_NO_APPROVE}</Text>
      </Stack>

      {controls.actions.length > 0 && (
        <Group gap="xs" data-backstop-controls="">
          {controls.actions.map((a) => (
            <Button
              key={a.action}
              size="xs"
              variant={open === a.action ? 'filled' : a.ends ? 'light' : 'default'}
              color={a.ends ? 'red' : undefined}
              disabled={act.isPending}
              onClick={() => {
                act.reset()
                setOpen(open === a.action ? null : a.action)
              }}
            >
              {a.label}
            </Button>
          ))}
        </Group>
      )}

      {controls.deniedReason && (
        <Text size="xs" c="dimmed" data-backstop-denied="">{controls.deniedReason}</Text>
      )}

      {spec && (
        <InterventionForm
          key={spec.action}
          spec={spec}
          detail={detail}
          pending={act.isPending}
          onCancel={() => setOpen(null)}
          onSend={(primary, then) => send(spec, primary, then)}
        />
      )}

      {act.isError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title="Not done" data-backstop-error="">
          <Text size="sm">{interventionErrorText(act.error)}</Text>
        </Alert>
      )}
    </Stack>
  )
}

/**
 * One intervention's form: what it does, and the one thing it needs — a reason, a note, a member, a route or a date.
 * Exported for the render tests.
 */
export function InterventionForm({ spec, detail, pending, onCancel, onSend }: {
  spec: InterventionSpec
  detail: BackstopRequestDetail
  pending: boolean
  onCancel: () => void
  onSend: (primary: BackstopAction, then: BackstopAction | null) => void
}) {
  const id = detail.request.id
  const [text, setText] = useState('')
  const [choice, setChoice] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const assignees = useBackstopAssignees(id, spec.input === 'assignee')

  const dueAt = date ? dueAtFromDateInput(date) : null
  const dateInvalid = date !== '' && dueAt === null

  const build = (): [BackstopAction, BackstopAction | null] | null => {
    switch (spec.action) {
      case 'force-close':
      case 'refuse':
        return text.trim() ? [{ action: spec.action, id, reason: text.trim() }, null] : null
      case 'forward':
      case 'attest':
        return [{ action: spec.action, id, note: text.trim() || null }, null]
      case 'reassign':
        return choice ? [{ action: 'reassign', id, assigneeUserId: choice }, null] : null
      case 'reroute':
        if (!choice || dateInvalid) return null
        return [{ action: 'reroute', id, routeTo: choice }, dueAt ? { action: 'due-date', id, dueAt } : null]
      case 'due-date':
        return dueAt && !dateInvalid ? [{ action: 'due-date', id, dueAt }, null] : null
    }
  }

  const ready = build()

  return (
    <Stack gap="xs" data-backstop-form={spec.action}>
      <Text size="sm">{spec.explain}</Text>

      {(spec.input === 'reason' || spec.input === 'note') && (
        <Textarea
          label={spec.input === 'reason' ? 'Reason (the requester reads it)' : 'Note (optional, for the audit row)'}
          required={spec.required}
          autosize
          minRows={2}
          maxLength={1000}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      )}

      {spec.input === 'assignee' && (
        <Select
          label="Hand it to"
          placeholder={assignees.isLoading ? 'Reading the members…' : 'A live member of the workspace'}
          data={(assignees.data?.items ?? [])
            .filter((m) => !m.isCurrentAssignee)
            .map((m) => ({ value: m.userId, label: `${m.displayName?.trim() || m.userId} (${m.role})` }))}
          value={choice}
          onChange={setChoice}
          nothingFoundMessage="Nobody else is a live member"
          searchable
        />
      )}

      {spec.input === 'route' && (
        <>
          <Select
            label="Send it to"
            data={detail.rerouteTo.map((r) => ({ value: r, label: routeLabel(r) }))}
            value={choice}
            onChange={setChoice}
          />
          <TextInput
            type="date"
            label="Then give them until (optional)"
            description="Without a date it stays on the backstop."
            value={date}
            onChange={(e) => setDate(e.currentTarget.value)}
            error={dateInvalid ? 'A day after today' : undefined}
          />
        </>
      )}

      {spec.input === 'date' && (
        <TextInput
          type="date"
          label="New due date"
          required
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          error={dateInvalid ? 'A day after today' : undefined}
        />
      )}

      <Group gap="xs">
        <Button
          size="xs"
          color={spec.ends ? 'red' : undefined}
          loading={pending}
          disabled={!ready || pending}
          onClick={() => ready && onSend(ready[0], ready[1])}
        >
          {spec.label}
        </Button>
        <Button size="xs" variant="subtle" onClick={onCancel} disabled={pending}>Cancel</Button>
      </Group>
    </Stack>
  )
}
